import { useState } from "react";
import { useTimeout } from "usehooks-ts";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import {
  MDSButton,
  MDSButtonVariant,
  MDSButtonSlotName,
  MDSIcon,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { useAppStore } from "@/store";
import { SettingsTabId } from "@/store/pages/SettingsPageStore/types";
import { CalendarConnectionsSection } from "@/components/calendar/calendar-connections-section";
import {
  SettingsIntegrationPageWrapper,
  SettingsIntegrationHeader,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
  SettingsIntegrationBenefitCard,
} from "@/pages/settings/components/settings-integration-layout";

const BENEFIT_POINTS = [
  "Connect any Google or Microsoft calendar",
  "See current and upcoming events in Mem, and get notified when meetings are about to start",
  "Have Mem record and take meeting notes for you in a single click",
  "Edit the notes Mem takes for you anytime",
];

export const SettingsCalendarPage = observer(() => {
  const { store } = useAppStore();
  const isConnected = store.calendar.google.isConnected || store.calendar.outlook.isConnected;
  const isLoading = !store.calendar.google.isReady || !store.calendar.outlook.isReady;

  const [isOAuthPending, setIsOAuthPending] = useState(false);

  useTimeout(() => setIsOAuthPending(false), isOAuthPending ? 5000 : null);

  return (
    <SettingsIntegrationPageWrapper>
      <SettingsIntegrationHeader
        breadcrumb="Apps and integrations"
        title="Calendar and meetings"
        isConnected={!isLoading ? isConnected : undefined}
      />

      <SettingsIntegrationScrollableArea>
        <SettingsIntegrationSection>
          <SettingsIntegrationBenefitCard points={BENEFIT_POINTS} />
        </SettingsIntegrationSection>

        <SettingsIntegrationSection>
          <CalendarConnectionsSection
            onConnectClick={() => setIsOAuthPending(true)}
            isOAuthPending={isOAuthPending}
          />
        </SettingsIntegrationSection>

        <SettingsIntegrationSection>
          <RelatedSettingsHeader>
            <MDSText
              variant={MDSTextVariant.TextXs}
              weight={MDSTextWeight.SemiBold}
              color={MDSTextColor.Secondary}
            >
              RELATED SETTINGS
            </MDSText>
          </RelatedSettingsHeader>
          <RelatedSettingsRow>
            <MDSButton
              variant={MDSButtonVariant.Default}
              onClick={() => store.settingsModal.navigateToTab(SettingsTabId.VoiceModePage)}
            >
              Voice mode settings
              <MDSIcon slot={MDSButtonSlotName.RightIcon} kind={MDSIconKind.ArrowUpRight} />
            </MDSButton>
            <MDSButton
              variant={MDSButtonVariant.Default}
              onClick={() =>
                store.settingsModal.navigateToTab(SettingsTabId.NotificationPreferencesPage)
              }
            >
              Meeting notification settings
              <MDSIcon slot={MDSButtonSlotName.RightIcon} kind={MDSIconKind.ArrowUpRight} />
            </MDSButton>
          </RelatedSettingsRow>
        </SettingsIntegrationSection>
      </SettingsIntegrationScrollableArea>
    </SettingsIntegrationPageWrapper>
  );
});

const RelatedSettingsHeader = styled.div`
  padding-top: ${({ theme }) => theme.spacing[4]};
  padding-bottom: 10px;
`;

const RelatedSettingsRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  max-width: 700px;

  @container mds-panel (max-width: 750px) {
    flex-direction: column;

    > * {
      width: 100%;
    }
  }
`;
